import { reportDeeplinkOpened, type DeeplinkType } from './mapSurfaceEvents'
import { reportAnalyticsEvent } from './webAnalyticsReporter'

/**
 * Web `deeplink_opened` entry-point attribution (catalog G-1). On web a
 * "deep link" is a cold load that lands on a content URL rather than the bare
 * map home — a shared / SERP `/stations/<slug>` link, or a route-builder URL
 * carrying `?from=&to=`. Both are `universal_link` entry points (the web
 * analogue of an iOS / Android universal link / app link). The bare map root
 * (`/` or `/:locale`) is a direct open, not a deep link → no event.
 *
 * Why capture at module load: the entry URL must be read BEFORE React Router
 * does any client-side navigation (which rewrites `location` immediately on
 * mount — e.g. SelectedStationUrlSync canonicalises the `?line=&station=`
 * params). The classification is frozen at first import; the EMIT is deferred
 * to consent (the sink is a no-op until the analytics SDKs load post-accept),
 * fired once via {@link recordWebDeeplinkOnce} from the consent `onAccept`
 * path — same shape as the launch event.
 *
 * `reportDeeplinkOpened` → `reportAnalyticsEvent` is production-gated +
 * fail-safe, so this never throws into startup.
 */

interface DeeplinkEntry {
  readonly type: DeeplinkType
  /** Resolved destination hint (station slug) when present; omitted otherwise. */
  readonly target?: string
}

/**
 * Classify a cold-load entry URL into a `deeplink_opened` payload, or `null`
 * when it is a direct open (bare map home, no route params). Pure — `path` and
 * `search` are passed in so it is unit-testable without touching `location`.
 *
 * - `/stations/<slug>` (optionally locale-prefixed) → `universal_link`, with
 *   `target` = the slug segment.
 * - any path carrying `?from=` or `?to=` (a shared route link) →
 *   `universal_link`, no `target`.
 * - everything else (the map root, `/lines`, `/oyster-card`, …) → `null`.
 */
export function classifyDeeplinkEntry(path: string, search: string): DeeplinkEntry | null {
  // `/stations/<slug>` — locale prefix is an optional leading `/<xx>` or
  // `/<xx-YY>` segment (matches the SPA's `/:locale/stations/:slug` shape).
  const stationMatch = path.match(/^(?:\/[a-z]{2}(?:-[A-Za-z]{2})?)?\/stations\/([^/]+)\/?$/)
  if (stationMatch) {
    return { type: 'universal_link', target: decodeURIComponent(stationMatch[1]!) }
  }
  const params = new URLSearchParams(search)
  if (params.has('from') || params.has('to')) {
    return { type: 'universal_link' }
  }
  return null
}

// Frozen at first import — the entry URL before any client-side navigation.
const entry: DeeplinkEntry | null =
  typeof window === 'undefined'
    ? /* v8 ignore next — SSR/prerender guard, unreachable in jsdom */ null
    : classifyDeeplinkEntry(window.location.pathname, window.location.search)

let deeplinkRecorded = false

/**
 * Emit `deeplink_opened` once per page load if the captured entry URL was a
 * deep link. No-op for a direct open (bare map home) or after the first call
 * (the `deeplinkRecorded` latch). Wired into the consent `onAccept` path so it
 * fires once the analytics SDKs load.
 */
export function recordWebDeeplinkOnce(): void {
  if (deeplinkRecorded) return
  deeplinkRecorded = true
  if (entry === null) return
  reportDeeplinkOpened(realFromSink, { type: entry.type, target: entry.target })
}

// The map-surface emitters take a `MetroAnalyticsReporter` (event, params).
// `reportAnalyticsEvent` already is that shape — forward directly so we reuse
// the one production-gated + fail-safe sink without importing the named
// `realMetroAnalyticsReporter` adapter (identical behaviour).
const realFromSink = (event: string, params: Record<string, unknown>): void =>
  reportAnalyticsEvent(event, params)

/* @internal — test-only reset of the one-shot latch. */
export function __resetDeeplinkForTests(): void {
  deeplinkRecorded = false
}
